import { useContext, useMemo } from "react";
import { IDefaultFailureType } from "entities/FailureDetailsEntity/FailureDetailsEntityInterfaces";
import { FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";
import { UserContext } from "contexts/UserContext/UserContext";
import { IEventReportDetails } from "entities/EventReports/EventReportsEntityInterfaces";
import { DetailsReportEditionContext } from "contexts/DetailsReportEditionContext/DetailsReportEditionContext";
import { FailureCodeField } from "../SegmentFields";

/**
 * Inline-edit adapter: binds the controlled FailureCodeField to the
 * DetailsReportEditionContext for the table's row edit.
 */
export const FailureCodeDetailsDropdown = ({
    defaultFailureTypes,
    row,
}: {
    defaultFailureTypes: IDefaultFailureType[];
    row: IEventReportDetails;
}) => {
    const { user_id } = useContext(UserContext);
    const { edit, setEdit } = useContext(DetailsReportEditionContext);

    const existingEdition = useMemo(() => edit.findIndex((event) => event.id === row.event_report_id), [edit, row]);
    const selectedFunctionalLocationId = useMemo(
        () => edit[existingEdition]?.changes?.functional_location_id,
        [edit, existingEdition],
    );
    const selectedFailureTypeIdIsNotSelected = useMemo(
        () =>
            row.failure_type.failure_type_id === edit[existingEdition]?.changes?.failure_type_id &&
            row.failure_type.code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
        [row, existingEdition, edit],
    );
    const selectedFailureTypeId = useMemo(
        () => (selectedFailureTypeIdIsNotSelected ? undefined : edit[existingEdition]?.changes?.failure_type_id),
        [existingEdition, edit, selectedFailureTypeIdIsNotSelected],
    );
    const selectedFailureCodeId = useMemo(
        () => edit[existingEdition]?.changes?.failure_code_id,
        [edit, existingEdition],
    );

    const handleFailureCodeUpdate = (id: number) => {
        if (existingEdition === -1) return;
        setEdit((prevEdit) => {
            const newEdit = [...prevEdit];
            newEdit[existingEdition] = {
                ...newEdit[existingEdition],
                changes: {
                    ...newEdit[existingEdition]?.changes,
                    failure_code_id: id,
                    ...(user_id && { edited_by_user_id: user_id }),
                },
            };
            return newEdit;
        });
    };

    return (
        <FailureCodeField
            machineCenterId={row.machine_center.machine_center_id}
            failureTypeId={selectedFailureTypeId}
            functionalLocationId={selectedFunctionalLocationId}
            locationSpecific={edit[existingEdition]?.location_specific}
            defaultFailureTypes={defaultFailureTypes}
            value={selectedFailureCodeId}
            obligatory={!selectedFailureTypeIdIsNotSelected}
            onChange={handleFailureCodeUpdate}
        />
    );
};
